import { useEffect, useState } from 'react';
import './css/Acp.css';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import ReceitaModal from './components/modalServices/ReceitaModal';
import usePagination10 from './components/hooks/usePagination10';
import useConfirmModal from './components/hooks/useConfirmModal';
import usePermissions from './components/hooks/usePermissions';
import { formatDateView } from './utils/dateUtils';
import { formatCurrencyView } from './utils/currencyUtils';
import { useNavigate } from 'react-router-dom';
import PageCard from './components/PageCard';
import useProgramaSituacao from './components/hooks/useProgramaSituacao';

function Receitas() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('receitas');
  
  // Campo único para pesquisa geral
  const [filtroGeral, setFiltroGeral] = useState('');
  const [receitasOriginais, setReceitasOriginais] = useState([]);
  const [atualizar, setAtualizar] = useState(0);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase =
    parseInt(localStorage.getItem('anoBase')) || new Date().getFullYear();
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [modalData, setModalData] = useState({
    show: false,
    type: '',
    message: '',
  });

  const { isProgramaAtivo } = useProgramaSituacao();

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isProgramaInativo, setIsProgramaInativo] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [showReceitaModal, setShowReceitaModal] = useState(false);
  const [receitaParaEditar, setReceitaParaEditar] = useState(null);

  const showDefaultModal = (type, message) => {
    setModalData({ show: true, type: type, message: message });
  };
  const closeDefaultModal = () => {
    setModalData(prevModalData => ({ ...prevModalData, show: false }));
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    setLoading(true);
    axios
      .get(`${API_BASE_URL.RECEITA}/all`, {
        params: {
          cnpjAssociacao: cnpj,
          ano: parseInt(anoBase),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        console.log('Receitas recebidas:', result.data);
        setReceitasOriginais(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter receitas:', error);
        setReceitasOriginais([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar, token, cnpj, anoBase, navigate, podeAbrir, carregandoPermissoes]);

  // Filtro dos dados
  function normalizeDate(dateStr) {
    if (!dateStr) return '';
    // Se vier no formato YYYY-MM-DD, retorna DD/MM/YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    }
    // Se vier no formato DD/MM/YYYY, retorna YYYY-MM-DD
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [d, m, y] = dateStr.split('/');
      return `${y}-${m}-${d}`;
    }
    return dateStr;
  }

  const receitasFiltradas = [...receitasOriginais].filter(c => {
    if (!filtroGeral) return true;
    const termo = filtroGeral.trim().toLowerCase();

    // Descrição
    const descricao = `${c.descricao || ''}`.toLowerCase();
    // Valor
    const valorStr =
      c.valor !== undefined && c.valor !== null
        ? String(c.valor).replace('.', ',')
        : '';
    // Data
    const dataStr = c.data || '';
    const dataNorm = normalizeDate(dataStr);
    const termoNorm = normalizeDate(termo);

    // Pesquisa em todos os campos
    if (
      descricao.includes(termo) ||
      valorStr.toLowerCase().includes(termo) ||
      dataStr.toLowerCase().includes(termo) ||
      dataNorm.toLowerCase().includes(termo) ||
      dataStr.toLowerCase().includes(termoNorm) ||
      String(c.id).includes(termo) ||
      String(c.numConta || '').includes(termo)
    ) {
      return true;
    }
    return false;
  });

  // Corrigir paginação para refletir o total de itens filtrados
  const totalItems = receitasFiltradas.length;
  const { setPagination } = usePagination10(totalItems);
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  // Ordena por id (decrescente por padrão)
  const receitasOrdenadas = receitasFiltradas.sort((a, b) => b.id - a.id);
  const selectedReceitas = receitasOrdenadas.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const handleSelectRecord = record => {
    const recordKey = `${record.ano}-${record.id}-${record.cnpjAssociacao}-${record.numConta}`;

    if (selectedRecord && selectedRecord.key === recordKey) {
      setSelectedRecord(null);
      setIsProgramaInativo(false);
    } else {
      setSelectedRecord({ key: recordKey, data: record });
      if (record.idPrograma) {
        const programaAtivo = isProgramaAtivo(record.idPrograma);
        setIsProgramaInativo(!programaAtivo);
      } else {
        setIsProgramaInativo(false);
      }
    }
  };

  useEffect(() => {
    if (!pendingSelection) {
      setSelectedRecord(null);
    }
  }, [receitasOriginais, pendingSelection]);

  const getSelectedRecord = () => {
    return selectedRecord ? selectedRecord.data : null;
  };

  const hasSelectedRecord = selectedRecord !== null;

  const handleReceitaSaveSuccess = (savedReceita, isEdit = false) => {
    setAtualizar(prev => prev + 1);

    if (savedReceita) {
      const selectionData = {
        id: savedReceita.id,
        ano: savedReceita.ano || new Date().getFullYear(),
        cnpjAssociacao: savedReceita.cnpjAssociacao || cnpj,
        numConta: savedReceita.numConta,
        isEdit: isEdit,
        isNewRecord: savedReceita.isNewRecord || false,
      };
      setPendingSelection(selectionData);

      // Após salvar, sempre volta para a primeira página
      setTimeout(() => {
        if (setPagination && setPagination.onGoToPage) {
          setPagination.onGoToPage(1);
        }
      }, 500);
    }
  };

  useEffect(() => {
    if (pendingSelection && receitasOriginais.length > 0) {
      let foundRecord = null;
      if (
        pendingSelection.id &&
        pendingSelection.id !== '' &&
        !pendingSelection.isNewRecord
      ) {
        foundRecord = receitasOriginais.find(item => {
          const matches =
            item.id === pendingSelection.id &&
            item.ano === pendingSelection.ano &&
            item.cnpjAssociacao === pendingSelection.cnpjAssociacao &&
            item.numConta === pendingSelection.numConta;

          return matches;
        });
      }

      if (
        !foundRecord &&
        (!pendingSelection.isEdit || pendingSelection.isNewRecord)
      ) {
        const sortedRecords = [...receitasOriginais].sort(
          (a, b) => b.id - a.id
        );

        if (sortedRecords.length > 0) {
          foundRecord = sortedRecords[0];
        }
      }

      if (foundRecord) {
        const recordKey = `${foundRecord.ano}-${foundRecord.id}-${foundRecord.cnpjAssociacao}-${foundRecord.numConta}`;

        const recordIndex = receitasOriginais.findIndex(
          item =>
            `${item.ano}-${item.id}-${item.cnpjAssociacao}-${item.numConta}` ===
            recordKey
        );

        if (recordIndex !== -1) {
          const targetPage = Math.floor(
            recordIndex / setPagination.rowsPerPage
          );
          const targetPageOneIndexed = targetPage + 1;

          setSelectedRecord({ key: recordKey, data: foundRecord });

          if (targetPageOneIndexed !== setPagination.currentPage) {
            setPagination.onGoToPage(targetPageOneIndexed);
          }
        } else {
          setSelectedRecord({ key: recordKey, data: foundRecord });
        }

        setPendingSelection(null);
      } else {
        setPendingSelection(null);
      }
    }
  }, [receitasOriginais, pendingSelection, setPagination]);

  function handleDelete(c) {
    if (!c) return;

    axios
      .delete(
        `${API_BASE_URL.RECEITA}/${c.ano}/${c.cnpjAssociacao}/${c.numConta}/${c.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then(() => {
        setAtualizar(prev => prev + 1);
        showDefaultModal('success', 'A receita foi excluída com sucesso!');
        setSelectedRecord(null);
      })
      .catch(error => {
        console.error('Erro ao excluir receita:', error);
        showDefaultModal('error', 'Erro ao excluir a receita.');
      });
  }

  const handleDeleteSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      showConfirmModal('delete', () => handleDelete(record));
    }
  };

  const handleEditSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      setReceitaParaEditar(record);
      setShowReceitaModal(true);
    }
  };

  const handleNewReceita = () => {
    setReceitaParaEditar(null);
    setShowReceitaModal(true);
  };

  const handleReceitaModalClose = () => {
    setShowReceitaModal(false);
    setReceitaParaEditar(null);
  };

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="Receitas"
      icon="bi-cash-stack"
      scrollable={false}
      footer={totalItems > 0 ? <Pagination10 {...setPagination} /> : null}
    >
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Acesso Negado</strong>
          <p className="mb-0 mt-2">
            Você não tem permissão para acessar o módulo de Receitas.
          </p>
          <p className="mb-0 mt-2">
            <small>Entre em contato com o administrador do sistema.</small>
          </p>
          <button 
            className="btn btn-sm btn-outline-secondary mt-3"
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Voltar
          </button>
        </div>
      ) : (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <button
            onClick={handleNewReceita}
            className="btn btn-success btn-sm d-inline-flex align-items-center"
            disabled={!podeCriar}
            title={!podeCriar ? 'Você não tem permissão para criar receitas' : 'Nova receita'}
          >
            <i className="bi bi-plus-lg me-1" />
            Novo
          </button>
          <button
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            onClick={handleEditSelected}
            disabled={!podeEditar || !hasSelectedRecord || isProgramaInativo}
            title={!podeEditar ? 'Você não tem permissão para editar' : !hasSelectedRecord ? 'Selecione um registro' : isProgramaInativo ? 'Programa ENCERRADO - não é possível editar' : 'Editar receita selecionada'}
          >
            <i className="bi bi-pencil me-1" />
            Editar
          </button>
          <div className="vr mx-1" />
          <button
            className="btn btn-danger btn-sm d-inline-flex align-items-center"
            onClick={handleDeleteSelected}
            disabled={!podeExcluir || !hasSelectedRecord || isProgramaInativo}
            title={!podeExcluir ? 'Você não tem permissão para excluir' : !hasSelectedRecord ? 'Selecione um registro' : isProgramaInativo ? 'Programa ENCERRADO - não é possível excluir' : 'Excluir receita selecionada'}
          >
            <i className="bi bi-trash me-1" />
            Excluir
          </button>

          <div className="ms-auto d-flex align-items-center">
            <div className="input-group" style={{ maxWidth: 300, height: 31 }}>
              <span className="input-group-text bg-white border-end-0 px-2" style={{ height: 31 }}>
                <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0"
                style={{ textTransform: 'uppercase', height: 31, fontSize: 13 }}
                placeholder="Pesquisar..."
                value={filtroGeral}
                onChange={e => setFiltroGeral(e.target.value.toUpperCase())}
              />
              {filtroGeral && (
                <button
                  className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                  style={{ height: 31 }}
                  onClick={() => setFiltroGeral('')}
                  title="Limpar pesquisa"
                  tabIndex={-1}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-striped table-sm">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                <th className="px-2 align-middle" style={{ width: '4%' }}>
                  Nº
                </th>
                <th className="px-2 align-middle" style={{ width: '10%' }}>
                  Data
                </th>
                <th className="px-2 align-middle" style={{ width: '8%' }}>
                  Conta
                </th>
                <th className="px-2 align-middle">Programa</th>
                <th className="px-2 align-middle">Descrição</th>
                <th
                  className="px-2 align-middle text-end"
                  style={{ width: '12%' }}
                >
                  Valor
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                selectedReceitas.map(c => {
                  const recordKey = `${c.ano}-${c.id}-${c.cnpjAssociacao}-${c.numConta}`;
                  const isSelected =
                    selectedRecord && selectedRecord.key === recordKey;

                  let rowClass = '';
                  let rowStyle = {
                    cursor: 'pointer',
                  };
                  if (isSelected) {
                    rowClass = 'table-active border-primary border-2';
                    rowStyle.backgroundColor = '#e3f2fd';
                    rowStyle.borderLeft = '4px solid #0d6efd';
                    rowStyle.boxShadow = '0 2px 4px rgba(13, 110, 253, 0.2)';
                  }

                  return (
                    <tr
                      key={recordKey}
                      className={rowClass}
                      style={rowStyle}
                      onClick={() => handleSelectRecord(c)}
                    >
                      <td className="px-2">{c.id}</td>
                      <td className="px-2">{formatDateView(c.data)}</td>
                      <td className="px-2">{c.numConta}</td>
                      <td className="px-2">{c.nomePrograma || '-'}</td>
                      <td className="px-2">{c.descricao}</td>
                      <td className="px-2 text-end">
                        {formatCurrencyView(c.valor)}
                      </td>
                    </tr>
                  );
                })}
              {!isLoading && receitasOriginais.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">
                    Nenhuma receita encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>
      )}

      <DefaultModal
        show={modalData.show}
        type={modalData.type}
        onClose={closeDefaultModal}
        message={modalData.message}
      />
      <ConfirmModal {...setConfirmModal} />
      <ReceitaModal
        show={showReceitaModal}
        onClose={handleReceitaModalClose}
        onSaveSuccess={handleReceitaSaveSuccess}
        receita={receitaParaEditar}
      />
    </PageCard>
  );
}

export default Receitas;
